import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { BookingsStatsComponent } from 'src/app/components/school/bookings-stats/bookings-stats.component';
import { LessonsStatsComponent } from 'src/app/components/school/lessons-stats/lessons-stats.component';
import { MaterialModule } from 'src/app/material.module';
import { AuthService } from 'src/app/services/auth.service';
import { SchoolService } from 'src/app/services/school.service';
import { StudentsService } from 'src/app/services/students.service';
import { ClassesChartComponent } from 'src/app/components/school/classes-stats/classes-chart.component';


interface DatePreset {
  key: string;
  label: string;
  from: () => Date;
  to: () => Date;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDatepickerModule, MatNativeDateModule, MaterialModule, LessonsStatsComponent, BookingsStatsComponent, ClassesChartComponent,],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private authService = inject(AuthService);
  private schoolService = inject(SchoolService);
  private studentService = inject(StudentsService);

  loading = false;
  kpis: any = null;
  lessonsToday: any = null;
  /**
   * The school's own currency (ISO code). The dashboard figures carry no
   * currency, and the bare `currency` pipe defaults to USD, so a euro school
   * read "$6,785.00". EUR (Kapay's market) until the school's own code loads.
   */
  currencyCode = 'EUR';
  bookingMovements: any = null;
  boxMovements: any = null;
  boxOccupancy: any = null;
  rentalKpis: any = null;
  rentalMovements: any = null;
  rentalProductStats: { active: number; hidden: number; total: number } | null = null;
  equipmentStats: { inService: number; maintenance: number; retired: number; total: number } | null = null;
  classesToday: any[] = [];
  lessonsChart: any[] = [];
  bookingsChart: any[] = [];

  activePreset = 'month';
  maxDate = new Date();
  minDate = (() => { const d = new Date(); d.setFullYear(d.getFullYear() - 2); return d; })();

  rangeForm = new FormGroup({
    start: new FormControl<Date | null>(null),
    end:   new FormControl<Date | null>(null),
  });

  presets: DatePreset[] = [
    {
      key: 'today', label: 'Today',
      from: () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: 'week', label: 'This Week',
      from: () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: 'month', label: 'This Month',
      from: () => { const d = new Date(); d.setDate(1); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: '3m', label: '3 Months',
      from: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: '6m', label: '6 Months',
      from: () => { const d = new Date(); d.setMonth(d.getMonth() - 6); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: '1y', label: '1 Year',
      from: () => { const d = new Date(); d.setFullYear(d.getFullYear() - 1); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
    {
      key: '2y', label: '2 Years',
      from: () => { const d = new Date(); d.setFullYear(d.getFullYear() - 2); d.setHours(0, 0, 0, 0); return d; },
      to:   () => { const d = new Date(); d.setHours(23, 59, 59, 999); return d; },
    },
  ];

  get periodLabel(): string {
    const preset = this.presets.find(p => p.key === this.activePreset);
    if (preset) return preset.label;
    const { start, end } = this.rangeForm.value;
    if (start && end) {
      return `${start.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${end.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    }
    return 'Custom';
  }

  get incidenceToday(): number {
    return (this.lessonsToday?.postponed ?? 0) + (this.lessonsToday?.cancelled ?? 0);
  }

  lessonStatusLabel(status: number): string {
    const map: Record<number, string> = {
      1: 'Scheduled', 2: 'In Progress', 3: 'Completed', 4: 'Postponed', 5: 'Cancelled',
    };
    return map[status] ?? '—';
  }

  lessonStatusClass(status: number): string {
    const map: Record<number, string> = {
      1: 'text-primary', 2: 'text-warning', 3: 'text-success', 4: 'text-accent', 5: 'text-error',
    };
    return map[status] ?? 'text-muted';
  }

  get groupedClasses(): { activityName: string; count: number; totalStudents: number; classes: any[] }[] {
    const map = new Map<string, { activityName: string; count: number; totalStudents: number; classes: any[] }>();

    for (const c of this.classesToday) {
      const key = c.activityName || 'Unknown';
      if (!map.has(key)) {
        map.set(key, { activityName: key, count: 0, totalStudents: 0, classes: [] });
      }
      const group = map.get(key)!;
      group.count++;
      group.totalStudents += c.studentCount ?? 0;
      group.classes.push(c);
    }

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }


  applyPreset(preset: DatePreset): void {
    this.activePreset = preset.key;
    const from = preset.from();
    const to   = preset.to();
    this.rangeForm.setValue({ start: from, end: to });
    this.loadDashboard(from, to);
  }

  private loadSchoolCurrency(): void {
    const schoolId = this.authService.userSig()?.schoolId;
    if (!schoolId) return;
    this.schoolService.getSchool(schoolId).subscribe({
      next: (school: any) => {
        const code = school?.currency ?? school?.data?.currency;
        if (code) this.currencyCode = code;
      },
      error: () => { /* keep the fallback */ },
    });
  }

  onCustomRangeApply(): void {
    const { start, end } = this.rangeForm.value;
    if (start && end) {
      this.activePreset = 'custom';
      const to = new Date(end);
      to.setHours(23, 59, 59, 999);
      this.loadDashboard(start, to);
    }
  }

  loadDashboard(from: Date, to: Date): void {
    const schoolId = this.authService.userSig()?.schoolId;
    if (!schoolId) return;
    this.loading = true;
    this.schoolService.getDashboard(schoolId, from.toISOString(), to.toISOString()).subscribe({
      next: (res) => {
        this.kpis             = res.kpis;
        this.lessonsToday     = res.lessonsToday;
        this.bookingMovements = res.bookingMovements;
        this.boxMovements     = res.boxMovements;
        this.boxOccupancy     = res.boxOccupancy;
        this.rentalKpis         = res.rentalKpis;
        this.rentalMovements    = res.rentalMovements;
        this.rentalProductStats = res.rentalProductStats ?? null;
        this.equipmentStats     = res.equipmentStats     ?? null;
        this.classesToday       = res.classesToday || [];
        this.lessonsChart     = res.charts?.lessonsCompletedPerDay || [];
        this.bookingsChart    = res.charts?.bookingsConfirmedPerDay || [];
        this.loading = false;
      },
      error: () => { this.loading = false; },
    });
  }




  ngOnInit(): void {
    this.loadSchoolCurrency();
    const monthPreset = this.presets.find(p => p.key === 'month')!;
    const from = monthPreset.from();
    const to   = monthPreset.to();
    this.rangeForm.setValue({ start: from, end: to });
    this.loadDashboard(from, to);
  }
}
